/*
 * BuilderScopingPane — problem scoping, run by the builder's Orbit plugin and
 * rendered here.
 *
 * This is the planner-style pane, NOT the dossier machine. The plugin's outputs
 * have their own shapes — a tappable question card, catalog matches, a paid
 * proposal with a payment link, a PRD document — and each keeps that shape
 * instead of being flattened into a field-by-field question. It lives inside the
 * existing Orbit shell, so the user experiences one continuous conversation.
 *
 * The person is already signed in and their company already known, so this pane
 * never asks who they are. If the workspace isn't connected to the builder we
 * say so plainly rather than quietly scoping into nowhere.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import './styles/scoping-pane.css';
import {
  fetchScopingState,
  sendScopingTurn,
  formatQuestionAnswers,
  ScopingUnavailable,
  type ScopingEvent,
  type ScopingQuestion,
} from './lib/scopingPluginApi';

const OPENER =
  "Tell me the problem you want to solve — what's draining the team, or what's broken today?";
const OPENER_WHY =
  "Describe it in your own words. I'll pull out what I need and only ask what's still unclear.";

/** A question card. Answers post back as the next message, so the plugin reads
 *  them exactly as it reads typed text. */
const QuestionCard: React.FC<{
  questions: ScopingQuestion[];
  disabled: boolean;
  onSubmit: (text: string) => void;
}> = ({ questions, disabled, onSubmit }) => {
  const [picked, setPicked] = useState<Record<number, string[]>>({});
  const [sent, setSent] = useState(false);

  const toggle = (qi: number, label: string, multi: boolean) => {
    setPicked((prev) => {
      const current = prev[qi] || [];
      if (!multi) return { ...prev, [qi]: [label] };
      if (current.includes(label)) return { ...prev, [qi]: current.filter((l) => l !== label) };
      return { ...prev, [qi]: [...current, label] };
    });
  };

  const answeredAll = questions.every((_, i) => (picked[i] || []).length > 0);

  const submit = () => {
    if (sent || disabled || !answeredAll) return;
    setSent(true);
    onSubmit(formatQuestionAnswers(questions, picked));
  };

  return (
    <div className="aa-plug-card">
      {questions.map((q, qi) => (
        <div key={qi} className="aa-plug-q">
          <div className="aa-inquiry-prompt-row">
            <span className="aa-inquiry-prefix">Orbit asks</span>
            <span className="aa-inquiry-prompt">{q.question}</span>
          </div>
          {q.header && (
            <div className="aa-inquiry-why">
              <span className="aa-inquiry-why-label">About:</span> {q.header}
            </div>
          )}
          <div className="aa-inquiry-controls">
            <div className="aa-inquiry-chiprow">
              {q.options.map((o) => {
                const on = (picked[qi] || []).includes(o.label);
                return (
                  <button
                    key={o.label}
                    type="button"
                    disabled={sent || disabled}
                    aria-pressed={on}
                    className={`aa-chip${on ? ' aa-chip--on' : ''}`}
                    onClick={() => toggle(qi, o.label, !!q.multiSelect)}
                    title={o.description || undefined}
                  >
                    <span className="aa-chip-label">{o.label}</span>
                    {o.recommended && <span className="aa-chip-sub">recommended</span>}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      ))}
      <div className="aa-inquiry-controls">
        <button
          type="button"
          className="aa-btn aa-btn-primary aa-btn-sm"
          disabled={sent || disabled || !answeredAll}
          onClick={submit}
        >
          {sent ? 'Sent' : 'Continue'}
        </button>
      </div>
    </div>
  );
};

/** Very small markdown rendering for the PRD: headings, bold, list items and
 *  paragraphs. Deliberately not a full parser — the PRD is generated by our own
 *  backend, and pulling in a markdown dependency for one surface isn't worth it. */
const PrdBody: React.FC<{ markdown: string }> = ({ markdown }) => {
  const lines = markdown.split('\n');
  return (
    <div className="aa-plug-prd-body">
      {lines.map((raw, i) => {
        const line = raw.trimEnd();
        if (!line.trim()) return <div key={i} className="aa-plug-prd-gap" />;
        // A markdown rule; it was showing through as literal dashes.
        if (/^\s*(?:-{3,}|\*{3,}|_{3,})\s*$/.test(line)) {
          return <hr key={i} className="aa-plug-prd-hr" />;
        }
        const heading = /^(#{1,4})\s+(.*)$/.exec(line);
        if (heading) {
          const level = Math.min(heading[1].length + 1, 5);
          const Tag = (`h${level}` as unknown) as keyof JSX.IntrinsicElements;
          return <Tag key={i} className="aa-plug-prd-h">{inline(heading[2])}</Tag>;
        }
        const bullet = /^\s*[-*]\s+(.*)$/.exec(line);
        if (bullet) return <li key={i} className="aa-plug-prd-li">{inline(bullet[1])}</li>;
        return <p key={i} className="aa-plug-prd-p">{inline(line)}</p>;
      })}
    </div>
  );
};

/** Bold and italic spans. Both appear in the brief Orbit writes, and an
 *  unhandled one shows through as literal asterisks. */
function inline(text: string): React.ReactNode {
  const parts = text.split(/(\*\*[^*]+\*\*|\*[^*\n]+\*|`[^`\n]+`)/g);
  return parts.map((part, i) => {
    const bold = /^\*\*([^*]+)\*\*$/.exec(part);
    if (bold) return <strong key={i}>{bold[1]}</strong>;
    const code = /^`([^`]+)`$/.exec(part);
    if (code) return <code key={i} className="aa-plug-prd-code">{code[1]}</code>;
    const italic = /^\*([^*]+)\*$/.exec(part);
    if (italic) return <em key={i}>{italic[1]}</em>;
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });
}

const BuilderScopingPane: React.FC = () => {
  const [events, setEvents] = useState<ScopingEvent[]>([]);
  const [problemId, setProblemId] = useState<string | null>(null);
  const [showOpener, setShowOpener] = useState(false);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [blocked, setBlocked] = useState<string | null>(null);
  const bottom = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const s = await fetchScopingState();
        if (!alive) return;
        setEvents(s.events);
        setProblemId(s.problem_id);
        setShowOpener(s.fresh);
      } catch (e) {
        if (!alive) return;
        if (e instanceof ScopingUnavailable) setBlocked(e.message);
        else setError('Could not load your scoping. Please try again.');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  useEffect(() => { bottom.current?.scrollIntoView({ block: 'end' }); }, [events, busy]);

  const send = useCallback(async (text: string) => {
    const message = text.trim();
    if (!message || busy) return;
    setBusy(true);
    setError(null);
    setShowOpener(false);
    setInput('');
    // Show what they said immediately — the turn can take a while.
    setEvents((prev) => [...prev, { kind: 'text', text: message, role: 'user' }]);
    try {
      const r = await sendScopingTurn(message, problemId);
      setEvents((prev) => [...prev, ...r.events]);
      if (r.problem_id) setProblemId(r.problem_id);
    } catch (e) {
      if (e instanceof ScopingUnavailable) setBlocked(e.message);
      else setError('That turn did not go through. Please try again.');
    } finally {
      setBusy(false);
    }
  }, [busy, problemId]);

  if (loading) return <div className="aa-plug-loading">Loading your scoping…</div>;

  if (blocked) {
    return (
      <div className="aa-plug-blocked">
        <h3>Scoping isn’t connected yet</h3>
        <p>{blocked}</p>
      </div>
    );
  }

  // The last question card is the live one; earlier cards are already answered.
  const lastQuestionIdx = (() => {
    for (let i = events.length - 1; i >= 0; i -= 1) if (events[i].kind === 'question') return i;
    return -1;
  })();

  return (
    <>
      <section className="aa-pane-stage">
        <div className="aa-plug-thread">
          {showOpener && (
            <div className="aa-turn aa-turn--orbit">
              <div className="aa-turn-byline"><span>Orbit</span></div>
              <p className="aa-plug-text">{OPENER}</p>
              <div className="aa-inquiry-why aa-plug-why">
                <span className="aa-inquiry-why-label">Why we ask:</span> {OPENER_WHY}
              </div>
            </div>
          )}

          {events.map((e, i) => {
            if (e.kind === 'text') {
              if (e.role === 'user') {
                return (
                  <div key={i} className="aa-pane-prev-user">
                    <span className="aa-pane-prev-user-label">You said</span>
                    <p className="aa-pane-prev-user-text">{e.text}</p>
                  </div>
                );
              }
              return (
                <div key={i} className="aa-turn aa-turn--orbit">
                  <div className="aa-turn-byline"><span>Orbit</span></div>
                  <PrdBody markdown={e.text} />
                </div>
              );
            }
            if (e.kind === 'working') {
              return (
                <div key={i} className="aa-turn aa-turn--system aa-turn--info aa-plug-working">
                  <span className="aa-turn-system-dot" aria-hidden="true" />
                  {e.label}
                </div>
              );
            }
            if (e.kind === 'question') {
              return (
                <QuestionCard
                  key={i}
                  questions={e.questions}
                  disabled={busy || i !== lastQuestionIdx}
                  onSubmit={send}
                />
              );
            }
            if (e.kind === 'prd_ready') {
              return (
                <div key={i} className="aa-plug-card aa-plug-out aa-plug-out--ready">
                  <span className="aa-plug-out-label">Scoping complete</span>
                  <p className="aa-plug-text">
                    Your build brief is saved. Open it from your dashboard whenever
                    you’re ready to start the build.
                  </p>
                  <a className="aa-btn aa-btn-primary aa-btn-sm aa-plug-cta" href="/dashboard">
                    Go to my dashboard
                  </a>
                </div>
              );
            }
            if (e.kind === 'prd') {
              return (
                <div key={i} className="aa-plug-card aa-plug-out aa-plug-out--prd">
                  <span className="aa-plug-out-label">Your build brief</span>
                  <h3 className="aa-plug-prd-title">{e.title}</h3>
                  <PrdBody markdown={e.markdown} />
                </div>
              );
            }
            if (e.kind === 'marketplace') {
              return (
                <div key={i} className="aa-plug-card aa-plug-out aa-plug-out--marketplace">
                  <span className="aa-plug-out-label">What already exists</span>
                  {e.matches.length === 0 ? (
                    <p className="aa-plug-text">
                      Nothing in the catalog covers this one, so we’ll scope it as a build.
                    </p>
                  ) : (
                    <div className="aa-plug-matches">
                      {e.matches.map((m, mi) => (
                        <div key={mi} className="aa-plug-match">
                          <span className="aa-plug-match-name">{m.name}</span>
                          {typeof m.why === 'string' && (
                            <span className="aa-plug-match-why">{m.why}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            }
            return (
              <div key={i} className={`aa-plug-card aa-plug-out aa-plug-out--${e.kind}`}>
                <span className="aa-plug-out-label">Talk to a specialist</span>
                <PrdBody markdown={(e as { text?: string }).text || ''} />
              </div>
            );
          })}

          {busy && (
            <div className="aa-turn aa-turn--system aa-plug-working is-live">
              <span className="aa-turn-system-dot" aria-hidden="true" />
              Working on that…
            </div>
          )}
          {error && <div className="aa-plug-error">{error}</div>}
          <div ref={bottom} />
        </div>
      </section>

      <div className="aa-pane-composer">
        <div className="aa-pane-composer-inner">
          <form
            className="aa-inquiry-textrow aa-inquiry-textrow--tall aa-plug-composer"
            onSubmit={(ev) => { ev.preventDefault(); send(input); }}
          >
            <textarea
              className="aa-inquiry-textarea"
              value={input}
              onChange={(ev) => setInput(ev.target.value)}
              onKeyDown={(ev) => {
                if (ev.key === 'Enter' && !ev.shiftKey) { ev.preventDefault(); send(input); }
              }}
              placeholder={busy ? 'Orbit is working…' : 'Type your answer…'}
              rows={2}
              disabled={busy}
            />
            <button
              type="submit"
              className="aa-btn aa-btn-primary aa-btn-sm"
              disabled={busy || !input.trim()}
            >
              Send
            </button>
          </form>
        </div>
      </div>
    </>
  );
};

export default BuilderScopingPane;
